/**
 * @raad/live-markers — the shared marker-model types (seam S2).
 *
 * Moved VERBATIM from web/src/features/live/types.ts (the marker half) and from
 * web/src/features/vehicles/useVehicleDetail.ts (the VehicleType enum + guard),
 * so the vehicle-state vocabulary and the vehicle-type enum have ONE owner that
 * both the customer map (web/) and the staff Global-live map (admin/) consume.
 * Behaviour is byte-identical to the pre-move web modules; web re-exports these
 * names so every existing importer is unchanged.
 *
 * `LiveResponse` (the /api/vehicles/live envelope) stays in web — it is the
 * tenant fetch contract, not part of the marker model.
 *
 * Two freshness questions, deliberately kept apart — the split is slice 06's
 * ruling and the map makes it sharpest:
 *
 *   - `positionFixedAt` / `positionAgeSec` — WHERE the vehicle was last located
 *     (Traccar `fixTime`). The ONLY customer-facing location freshness.
 *   - `trackerContactAt` — when the tracker last made contact (`lastUpdate`).
 *
 * `speedKmh` is ALREADY converted from knots server-side. Never convert again.
 */

/**
 * The fixed eleven-member vehicle category (home of the enum since slice 84;
 * `three_wheeler` slice 55, `land_cruiser` slice 58). An unexpected wire value
 * renders as `other` rather than throwing a missing-i18n-key on a customer's
 * screen. Single source for the guard AND the type-label loop.
 */
export type VehicleType =
  | 'sedan'
  | 'suv'
  | 'land_cruiser'
  | 'pickup'
  | 'van'
  | 'truck'
  | 'tanker'
  | 'bus'
  | 'motorcycle'
  | 'three_wheeler'
  | 'other';

export const VEHICLE_TYPES: readonly VehicleType[] = [
  'sedan',
  'suv',
  'land_cruiser',
  'pickup',
  'van',
  'truck',
  'tanker',
  'bus',
  'motorcycle',
  'three_wheeler',
  'other',
] as const;

export function normalizeVehicleType(value: unknown): VehicleType {
  return typeof value === 'string' && (VEHICLE_TYPES as readonly string[]).includes(value)
    ? (value as VehicleType)
    : 'other';
}

export type VehicleStatus = 'online' | 'offline' | 'unknown';
/**
 * The MAP's answer to "what is the vehicle DOING" — five values, mirroring the
 * API's `MotionZ` (api/src/modules/live/view.ts) verbatim. `unknown` = tracker in
 * contact but the fix is too old to state motion; the API emits it structurally so
 * it can never claim a motion it cannot support (Mahad's ruling). It is spelled
 * the same as `VehicleStatus`'s `unknown` but answers a DIFFERENT question — the
 * two never share a colour, label or code path (each has its own token + i18n key).
 */
export type Motion = 'moving' | 'idle' | 'stopped' | 'offline' | 'unknown';
/**
 * VEHICLE STATE — seam S1 (slice 13e), the product's single vehicle-state
 * vocabulary. Answers "what is the vehicle DOING", derived server-side from
 * ignition + speed + freshness, and it is DELIBERATELY non-overlapping with the
 * tracker-state vocabulary (`status`, re-worded to Reporting/Not reporting) and
 * with the position-age measurement — no word is shared, so a customer can never
 * read one fact as another (Q19 / "Unknown is the default state of a parked
 * vehicle").
 *
 *   - `moving` | `idling` | `parked` | `engineOn` — the four states that make an
 *     honest vehicle-state CLAIM and print a word.
 *   - `noContact` | `unknown` — make NO vehicle-state claim. A surface renders the
 *     MEASUREMENTS for these (tracker state, position age), NEVER a state word, and
 *     NEVER the word "Unknown" (the whole defect this slice removes). `engineOn`
 *     (ignition on, fix stale) is the case Mahad cares most about — it could mean a
 *     vehicle moving where we cannot see it — so it is given visual prominence and
 *     never hides inside a measurement.
 *
 * Supersedes `motion` as the customer-facing vocabulary. `motion` is retained ONLY
 * as the marker/dot visual tone (`markerTone`), never rendered as a word again.
 */
/** Mirrors `VehicleStateZ` in api/src/modules/live/view.ts — seam S1. `engineStale`
 *  (15e) is "Engine state unknown": last seen DRIVING, telemetry since gone quiet
 *  past the transition budget. A new member here is a COMPILE ERROR at every
 *  `Record<VehicleState, …>` below until handled, which is the seam's mechanism. */
export type VehicleState =
  | 'moving'
  | 'idling'
  | 'parked'
  | 'engineOn'
  | 'engineStale'
  | 'noContact'
  | 'unknown';
export type VehiclePlan = 'track' | 'fleet' | 'pro';

/**
 * TRACKER HEALTH — seam S1, the object raad-api adds to `VehicleLiveItem`. It is
 * NOT vehicle state and never collapses into `motion`: a vehicle can be `stopped`
 * or `unknown` (fix stale) while its tracker is healthy, in contact and reporting
 * a full battery. Slice 13d renders the two apart — the defect this slice exists
 * to fix (Q19) was that `unknown` was the only thing a parked-under-cover vehicle
 * ever said.
 *
 * THE NULLABILITY IS THE DESIGN, not an edge case, and the two absent shapes must
 * render DIFFERENTLY (see `trackerHealthView`):
 *
 *   - `trackerHealth: null`        — no tracker telemetry at all.
 *   - object present, every field null — the tracker IS reporting but carried no
 *     readings in this report.
 *
 * Each field is independently absent. A null field renders NOTHING for that fact —
 * never a `0`, a `—`, or any placeholder implying a reading we do not hold. The two
 * power facts are DELIBERATELY separate and never merged into one "battery": on this
 * hardware they answer different questions —
 *
 *   - `batteryVolts`    — the tracker's own internal BACKUP cell VOLTAGE (V).
 *     Answers "will the tracker keep reporting if it loses vehicle power". It is
 *     VOLTS, never a percentage: a real FMC920 fix (device 3, 19 Aug 2026) carried
 *     `battery: 3.798` — a 3.798 V Li-ion cell — and NO `batteryLevel` key. Rendered
 *     plainly in volts, with no band/level: a voltage→health threshold for this cell
 *     would be an unmeasured guess, and CLAUDE.md forbids shipping one.
 *   - `externalVoltage` — the VEHICLE's supply (volts). Its collapse to ~0 is an
 *     anti-theft / power-cut signal, which is why it must never be averaged into a
 *     single battery indicator.
 *
 * `reportedAt` is OUR clock's timestamp of the last telemetry/position report —
 * rendered absolute (wall-clock), never as a client-recomputed age, for the same
 * reason the freshness line renders contact timestamps absolute: a wrong phone clock
 * must not be able to drift it. `trackerContactAt` lives on `LiveVehicle` already and
 * is a separate signal — do not expect it inside this object.
 */
export interface TrackerHealth {
  /** Internal backup cell voltage, V (not a percentage). Null when the report carried no battery reading. */
  batteryVolts: number | null;
  /** Vehicle supply, volts. Null when unreported; ~0 is a power-cut signal, not merged with battery. */
  externalVoltage: number | null;
  /**
   * 🔴 SLICE 56b — the device's connected-to-vehicle-power BOOLEAN (`charge`). The
   * gt06/VL110C FALLBACK for vehicle power: when `externalVoltage` is null (a VL110C
   * that has not yet sent its 10-min ADT voltage frame) the card shows on-vehicle-
   * power vs on-battery from this instead. Null when unreported. NEVER derived from
   * `externalVoltage`, and never rendered as a voltage.
   */
  charge: boolean | null;
  /**
   * 🔴 SLICE 56b — the tracker's backup battery as a PERCENT (`batteryLevel`, VL110C
   * status frames). Distinct from `batteryVolts` (FMC920 backup-cell VOLTAGE) and
   * NEVER inter-converted — each device reports its own unit, and the card shows what
   * was reported, nothing when nothing was. Null when unreported.
   */
  batteryLevelPct: number | null;
  /** GSM signal strength on Teltonika's own 0–5 scale (a real fix carried 5), NOT dBm.
   *  Rendered with its scale (e.g. 5/5), never as a bare unitless integer. */
  rssi: number | null;
  /** Satellites in the last fix — a low count is exactly the "parked under cover" story. */
  sats: number | null;
  /** ISO-8601, OUR clock's time of the last report. Rendered absolute, never recomputed to an age. */
  reportedAt: string | null;
}

export interface LiveVehicle {
  id: string;
  plate: string;
  label: string | null;
  model: string | null;
  plan: VehiclePlan;
  /** Body class — STABLE identity (slice 09/10b), already emitted on the /live
   * item (`VehicleLiveItem.vehicleType`). Drives the marker's SHAPE only; the
   * motion tone drives its colour, and the two never read each other (PLAN S2).
   * Normalised on ingest so an absent/unknown value is `other` (→ the dot),
   * never a throw. Not volatile — `applyFrame` carries it through unchanged. */
  vehicleType: VehicleType;
  /** Tracker liveness — the LIST's question. Re-worded to the tracker-state
   *  vocabulary at the surface (online → Reporting, offline → Not reporting,
   *  unknown → render nothing); the wire values are unchanged. */
  status: VehicleStatus;
  /** Seam S1 (13e) — the vehicle-state vocabulary. THE customer-facing "what is it
   *  doing" answer; supersedes `motion` for every rendered word. */
  state: VehicleState;
  /** 15e seam — was the last stored fix at/above the moving threshold? Chooses
   *  `engineStale`'s sub-line. SERVER-DERIVED: /api/vehicles carries no speedKmh
   *  by design, so deriving it client-side would word the same fact differently on
   *  the list and the map. Null ⇒ no speed held ⇒ no sub-line. */
  lastFixMoving: boolean | null;
  /**
   * SLICE B — is there an OPEN overspeed episode for this vehicle right now?
   *
   * 🔴 SERVER-DERIVED, and it has to be. The client CANNOT compute this: the
   * threshold is resolved vehicle-override → tenant-config → SKIP and is never
   * shipped to the browser, and the detector carries hysteresis (an episode closes
   * only once speed falls 5 km/h BELOW the limit). A client-side
   * `speedKmh > threshold` would flicker red around the limit and disagree with the
   * alert feed the customer is reading beside the map — one fact, two answers, both
   * on screen. The flag mirrors `overspeed_vehicle_state`, so the marker and the
   * feed cannot diverge by construction.
   *
   * NEVER null. Absent/unknown coerces to `false`, so a missing field can only ever
   * fail to show red — never invent one.
   */
  speeding: boolean;
  /** @deprecated (13e) — no longer rendered as a word. Kept ONLY to drive the
   *  marker/dot visual tone (`markerTone`). Never print `t('livemap.motion.*')`. */
  motion: Motion;
  /** WGS84 degrees. Null when there is no cached position — the vehicle gets NO marker. */
  lat: number | null;
  lon: number | null;
  /** KM/H, already converted from knots server-side. */
  speedKmh: number;
  /** Heading 0–360, or `null` when the fix carries no course. Only meaningful (and
   *  only drawn) when moving; a null/non-finite course renders the dot, never a
   *  north-pointing silhouette (`resolveMarker`'s `Number.isFinite` guard). Both
   *  ingest paths carry null through unchanged so they decide identically — the
   *  fetch path via the wire value, `applyFrame` by coalescing a null frame to null. */
  course: number | null;
  /**
   * Seam S3-W — the last heading observed while the vehicle was MOVING, retained
   * unchanged while it is stopped and updated only by a moving fix. Degrees 0–360,
   * or `null` when no moving fix has ever been seen.
   *
   * A stopped/idle/unknown marker rotates to THIS, not to its current fix's
   * `course`: proven in production, our OsmAnd phone (device 4) collapses to
   * `course 0` the instant it stops — `course 0.0` is its single most common value
   * — so rotating a parked marker to its current course would point every
   * phone-tracked stopped vehicle due north, the exact case Mahad banned. The
   * pilot's FMC920 retains its heading, but the client cannot tell the two trackers
   * apart, so it never trusts a stopped fix's course.
   *
   * `null` is therefore load-bearing: it renders the DOT, never an unrotated (due
   * north) silhouette. It is NEVER defaulted to 0 — 0 is a real heading. On the
   * fetch path `useLiveVehicles` coalesces an absent/non-finite value to `null`
   * (`courseOrNull`); on the push path `applyFrame` RETAINS the prior baseline when
   * a frame omits it or sends null (the cold-buffer case), so a reconnect cannot
   * blank a parked vehicle's heading.
   */
  lastMovingCourse: number | null;
  /** ISO-8601 fixTime — when the vehicle was last LOCATED. */
  positionFixedAt: string | null;
  /** Server-computed seconds since the fix, clamped >= 0. The freshness value. */
  positionAgeSec: number | null;
  /** ISO-8601 lastUpdate — tracker contact recency. Diagnostic only. */
  trackerContactAt: string | null;
  /** True when position and tracker-contact freshness differ beyond threshold.
   * Carried on the wire; no longer rendered as a separate note — the freshness
   * line now states the contact-vs-position case directly (`freshness.ts`). */
  freshnessDiverged: boolean;
  /** Age of the cached Redis position record, seconds. */
  cacheAgeSec: number | null;
  /** True when the value came from the Redis live cache. */
  cached: boolean;
  /** Server's verdict that the position is too old to be treated as live. */
  stale: boolean;
  /**
   * Seam S1 — tracker health, separate from vehicle state. `null` means no tracker
   * telemetry; an object with all-null fields means "reporting, no readings". The two
   * are rendered differently (slice 13d). See `TrackerHealth`.
   */
  trackerHealth: TrackerHealth | null;
  /**
   * 18g — is a tracker FITTED to this vehicle right now?
   *
   * 🔴 AN ADDITIVE BOOLEAN, AND IT IS DELIBERATELY NOT A NEW `VehicleState` OR
   * `Motion` MEMBER. Both of those enums are persisted through localStorage (the
   * live seed) and read by exhaustive `Record<VehicleState, …>` maps; widening
   * either would change the meaning of bytes already sitting in customers'
   * browsers, in ways nothing here can bound. A boolean beside them changes
   * nothing that is already stored.
   *
   * 🔴 IT IS IDENTITY, NOT LIVENESS. `false` means the tracker is out of the
   * vehicle — away for repair, or moved — and the API enforces on the wire that
   * it implies no position and no live data at all. `true` with no position is
   * the ordinary state of a tracker that has not reported yet, which is a
   * completely different sentence and must never render as "No tracker".
   *
   * A vehicle with no tracker KEEPS its trips, its history and its detail page.
   * That is the entire point of the maintenance case.
   */
  hasTracker: boolean;
  /**
   * 13f seam S5 — ISO start of the vehicle's OPEN `tracker_offline` alert (the
   * alert row's `started_at`), or `null` when the tracker is reporting. The server
   * derives it from the tracker-health sweep (not from Redis freshness), so the UI
   * renders "Tracker silent since hh:mm" straight from this instant and derives
   * nothing. It is NOT carried on the live WS frame (a stale tracker produces no
   * frames), so `applyFrame` retains it from the initial fetch and the 60 s
   * reconcile refreshes it.
   */
  trackerSilentSince: string | null;
}
